import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Row, Col } from 'reactstrap';
import { notifyError } from '../../utils/notifier';
import messages from '../../constants/messages';
import { fetchTests } from '../../actions/tests/apiRequests';
import { fetchReportOptions } from '../../actions/companies/apiRequests';
import { deserializeTestV2 } from '../../actions/tests/serializers';
import withRoleRestrictions from '../shared/withRoleRestrictions';
import Page from '../shared/Page';
import Loader from '../shared/Loader';
import Pagination from '../shared/Pagination';
import TestsListTable from './TestsListTable';
import TestsListFilters from './TestsListFilters';
import withEulaRestrictions from '../license/withEulaRestrictions';
import { getBreadCrumbs } from '../../utils/breadCrumbs';

const PAGE_SIZE = 20;

export class TestsListPage extends React.Component {
  constructor(props) {
    super(props);
    const filter = JSON.parse(localStorage.getItem('searchFilter'));
    this.state = {
      tests: [],
      page: 1,
      size: 1,
      pageCount: 1,
      totalCount: 0,
      filters: {
        ClientName: filter ? filter.ClientName : '',
        Email: filter ? filter.Email : '',
        PostalCode: filter ? filter.PostalCode : '',
        Address: filter ? filter.Address : '',
        TesterName: filter ? filter.TesterName : '',
        TestId: filter ? filter.TestId : '',
        Start: filter && filter.Start ? new Date(filter.Start) : null,
        End: filter && filter.End ? new Date(filter.End) : null,
        completed: filter ? filter.completed : null,
        qc: filter ? filter.qc : null,
        CustomerNumber: filter ? filter.CustomerNumber : '',
        SerialNumber: filter ? filter.SerialNumber : '',
        OrderColumn: 'StartDate',
        OrderBy: true,
      },
      loading: true,
      defaultReportLayout: null,
    };
    this.fetchResults = this.fetchResults.bind(this);
    this.onChangePage = this.onChangePage.bind(this);
    this.onChangeFilter = this.onChangeFilter.bind(this);
    this.sortTable = this.sortTable.bind(this);
    this.clearFilters = this.clearFilters.bind(this);
  }

  static get propTypes() {
    return {
      isSpruceAdmin: PropTypes.bool,
      companyId: PropTypes.string,
      unitsOfMeasure: PropTypes.string,
      title: PropTypes.string,
      location: PropTypes.object,
      tzAbbr: PropTypes.string,
    };
  }

  componentDidMount() {
    const { page } = this.state;

    this.fetchDefaultReportLayout().then(() => {
      this.fetchResults(page);
    });
  }

  fetchResults(page) {
    this.setState({ loading: true });

    return fetchTests(page, PAGE_SIZE, this.state.filters)
      .then(({ data, page, size, pageCount, totalCount }) =>
        this.setState({
          tests: data.map(deserializeTestV2),
          page,
          size,
          pageCount,
          totalCount,
          loading: false,
        }),
      )
      .catch(() => {
        this.setState({ loading: false });
        notifyError(messages.request.testsListError);
      });
  }

  fetchDefaultReportLayout() {
    const { companyId } = this.props;

    if (!companyId) {
      return Promise.resolve();
    }

    return fetchReportOptions(companyId).then(({ defaultLayout }) => {
      this.setState({
        defaultReportLayout: defaultLayout,
      });
    });
  }

  onChangePage(page = 1) {
    window.scrollTo(0, 0);
    this.fetchResults(page).then(this.setState({ page }));
  }

  onChangeFilter(filter, value) {
    this.setState(
      (state) => ({
        filters: {
          ...state.filters,
          [filter]: value,
        },
      }),
      () => {
        localStorage.setItem('searchFilter', JSON.stringify(this.state.filters));
      },
    );
  }

  clearFilters() {
    this.setState(
      (state) => ({
        filters: {
          ...state.filters,
          ClientName: '',
          Email: '',
          PostalCode: '',
          Address: '',
          TesterName: '',
          TestId: '',
          Start: null,
          End: null,
          completed: null,
          qc: null,
          CustomerNumber: '',
          SerialNumber: '',
        },
      }),
      () => {
        localStorage.setItem('searchFilter', JSON.stringify(this.state.filters));
        this.fetchResults(this.state.page);
      },
    );
  }

  getBreadCrumbs(title) {
    const { pathname } = this.props.location;
    return getBreadCrumbs(title, pathname, []);
  }

  sortTable(sortingColumnName) {
    const { OrderBy, OrderColumn } = this.state.filters;
    this.setState(
      (state) => ({
        filters: {
          ...state.filters,
          OrderBy: OrderColumn === sortingColumnName ? !OrderBy : true,
          OrderColumn: sortingColumnName,
        },
        page: 1,
      }),
      () => {
        this.fetchResults(this.state.page);
      },
    );
  }

  render() {
    const { tests, page, size, totalCount, pageCount, filters, loading, defaultReportLayout } = this.state;
    const { isSpruceAdmin, unitsOfMeasure, title, tzAbbr } = this.props;
    const hasResults = tests && tests.length;
    const itemsFrom = page * size - size + 1;
    let itemTo = page * size;
    if (totalCount - page * size < 0) {
      itemTo = totalCount;
    }

    return (
      <Page header="Manage Tests" breadCrumbs={this.getBreadCrumbs(title)}>
        <Row>
          <Col>
            <TestsListFilters
              loading={loading}
              isSpruceAdmin={isSpruceAdmin}
              onChange={this.onChangeFilter}
              onSubmit={this.fetchResults}
              filters={filters}
              clearFilters={this.clearFilters}
            />
          </Col>
        </Row>
        <If condition={totalCount > 0}>
          <Row>
            <div className="ml-3 col-md-3">
              <p>{`${itemsFrom} — ${itemTo} out of ${totalCount} results`}</p>
            </div>
          </Row>
        </If>
        <Choose>
          <When condition={!loading && !hasResults}>
            <div className="text-center my-5 py-5">No tests found</div>
          </When>
          <Otherwise>
            <Row className="mb-2">
              <Col>
                <TestsListTable
                  isSpruceAdmin={isSpruceAdmin}
                  unitsOfMeasure={unitsOfMeasure}
                  reportLayout={defaultReportLayout}
                  tests={tests}
                  tzAbbr={tzAbbr}
                  sortTable={this.sortTable}
                  sortAsc={filters.OrderBy}
                  sortedColumn={filters.OrderColumn}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Choose>
                  <When condition={loading}>
                    <Loader />
                  </When>
                  <Otherwise>
                    <If condition={pageCount > 1}>
                      <Pagination
                        activePage={page}
                        itemsCountPerPage={PAGE_SIZE}
                        totalItemsCount={totalCount}
                        onChange={this.onChangePage}
                      />
                    </If>
                  </Otherwise>
                </Choose>
              </Col>
            </Row>
          </Otherwise>
        </Choose>
      </Page>
    );
  }
}

const mapStateToProps = ({ profile }) => ({
  isSpruceAdmin: profile.isSpruceAdmin,
  unitsOfMeasure: profile.uom,
  companyId: profile.companyId,
  tzAbbr: profile.tzAbbr,
});

export default connect(mapStateToProps)(withEulaRestrictions(withRoleRestrictions(TestsListPage)));



// WEBPACK FOOTER //
// src/components/testsList/TestsListPage.jsx